/**
 * The "Dark mode" row of the account menu: a nav row with a switch at its
 * right edge, as in WeHunt Web's sidebar. Like the prototype it reads
 * "Dark mode" with a moon while the app is light and "Light mode" with a sun
 * while it is dark; the accessible name stays "Dark mode" so the switch
 * semantics (checked = dark) do not flip with the label.
 *
 * Labels come from the caller: the sidebar reads the dictionary through a
 * hook, the header menu receives it as a prop.
 */

import { AccountNavRow } from "@/components/common/AccountNavRow";
import { useTheme } from "@/utils/theme";
import styles from "./ThemeToggleRow.module.css";

export interface ThemeToggleLabels {
  /** "Dark mode" — shown while light; also the switch's accessible name. */
  dark: string;
  /** "Light mode" — shown while dark. */
  light: string;
}

export interface ThemeToggleRowProps {
  labels: ThemeToggleLabels;
  size?: "md" | "lg";
}

export function ThemeToggleRow({ labels, size = "md" }: ThemeToggleRowProps) {
  const { isDark, toggleTheme } = useTheme();

  return (
    <AccountNavRow
      size={size}
      icon={isDark ? "lightMode" : "darkMode"}
      label={isDark ? labels.light : labels.dark}
      ariaLabel={labels.dark}
      switchChecked={isDark}
      onClick={toggleTheme}
      trailing={
        <span
          className={`${styles.track} ${isDark ? styles.trackOn : ""}`}
          aria-hidden="true"
        >
          <span className={styles.knob} />
        </span>
      }
    />
  );
}

export default ThemeToggleRow;
